// @flow
import React from 'react'
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import jStat from 'jStat'
import ReactTable from 'react-table'
import 'react-table/react-table.css'
import TableStyles from '../../../../er2_styles/attribute_table'
import ErrorBoundary from '../error_boundary'
import EditManager from './edit_manager'
import Statistics from './statistics'
import Calculator from './calculator'
import AddColumn from './add_column'
import JoinTable from './join_table'
import RenameColumn from './rename_column'
import CreateLayer from './create_layer'
import QueryBuilder from './query_builder'
import type { MapsourcesType } from '../../reducers/mapsources'
import type { AttributeTableType } from '../../reducers/attribute_tables'
import { appendToLogger } from '../../actions'

type PropsType = {
    appendToLogger: Function,
    attributeTable: AttributeTableType,
    canClose: boolean,
    classes: {
        root: string,
    },
    createLayerFromTable: Function,
    height: number,
    joinTable: Function,
    mapsources: MapsourcesType,
    saveEdits: Function,
    token: string,
    theme: TableStyles,
}

type StateType = {
    attributes: AttributeTableType[],
    calcColumn: string,
    // Upper left, lower right cell indices
    cellSelection: [[number, number], [number, number]],
    columnMenuActionAnchor: any,
    editable: boolean,
    editManager: EditManager,
    fetchingAttributes: boolean,
    filter: string,
    header: string[],
    isEditing: boolean,
    // Layer being edited
    layerName: string,
    // Mapsource with the layer or table being edited
    mapsourceName: string,
    openCalc: boolean,
    order: boolean,
    orderBy: string,
    page: number,
    rows: [{}],
    rowsPerPage: number,
    schema: {},
    selected: number[],
    showAddColumn: boolean,
    showCreateLayer: boolean,
    showJoinTable: boolean,
    showRenameColumn: boolean,
    showQueryBuilder: boolean,
    showStats: boolean,
    sortingColumnExtensions: [],
    tableMenuActionsAnchor: any,
    controlbarHeight: number,
    headerHeight: number,
    paginationHeight: number,
    rowHeight: number,
}

const copyToClipboard = (str) => {
    const el = document.createElement('textarea')
    el.value = str
    el.setAttribute('readonly', '')
    el.style.position = 'absolute'
    el.style.left = '-9999px'
    document.body.appendChild(el)
    const selected = document.getSelection().rangeCount > 0
        ? document.getSelection().getRangeAt(0)
        : false
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
    if (selected) {
        document.getSelection().removeAllRanges()
        document.getSelection().addRange(selected)
    }
}

class AttributeTable extends React.PureComponent<PropsType, StateType> {
    constructor(props: PropsType) {
        super(props)
        // Copy props into state for modification?
        this.state = {
            ...props.attributeTable,
            cellSelection: [[-1, -1]],
            columnMenuActionsAnchor: null,
            editable: false,
            editingStateColumnExtensions: [
                { columnName: 'id', editingEnabled: false },
            ],
            fetchingAttributes: true,
            filter: '',
            header: [],
            rows: [],
            showStats: false,
            sorting: [{ columnName: 'id', direction: 'asc' }],
            sortingStateColumnExtensions: [
                { columnName: 'id', sortingEnabled: true },
            ],
            tableMenuActionsAnchor: null,
            showAddColumn: false,
            showCreateLayer: false,
            showJoinTable: false,
            showRenameColumn: false,
            renameColumnName: '',
            modelessDialog: props.attributeTable.querying ? 'QUERY_BUILDER' : '',
        }

        this.editManager = null
        this.getAttributes()
    }

    componentWillReceiveProps(nextProps, nextContext) {
        if (nextProps.attributeTable.querying !== this.props.attributeTable.querying) {
            this.setState({
                modelessDialog: nextProps.attributeTable.querying ?
                    'QUERY_BUILDER' : '',
            })
        }

        // Check for no row height
        if (!this.state.rowHeight) {
            this.addRowHeightToState()
        }
    }

    createEM() {
        if (!this.editManager) {
            this.editManager = new EditManager(this.state.rows, this.props.attributeTable.schema, this.props.attributeTable.header)
            this.setState({ editable: true })
        }
    }

    destroyEM() {
        this.editManager = null
        this.setState({ editable: false })
    }

    getEM() {
        this.createEM()
        return this.editManager
    }

    hasEM() {
        return this.editManager !== null
    }

    addRowHeightToState() {
        if (!this.state.rowHeight && this.container) {
            const headers = this.container.getElementsByClassName('rt-thead')
            if (headers.length) {
                const headerHeight = Array.from(headers).reduce((height, h) => (height + h.clientHeight), 0)
                const pagination = this.container.getElementsByClassName('pagination-bottom')
                const paginationHeight = pagination[0].getBoundingClientRect().height
                const controlbar = this.container.getElementsByClassName('attributeTableControlBar')
                const controlbarHeight = controlbar[0].getBoundingClientRect().height
                const rows = this.container.getElementsByClassName('rt-tbody')[0].getElementsByClassName('rt-tr')
                if (rows.length) {
                    const rowHeight = rows[0].getBoundingClientRect().height
                    this.setState({ controlbarHeight, headerHeight, paginationHeight, rowHeight })
                }
            }
        }
    }

    getRowsPerPage(containerHeight) {
        // Return at least one row so that the react-table has a tr element for calculating height on the next go-around
        let nrows = 0
        const { controlbarHeight, headerHeight, paginationHeight, rowHeight } = this.state
        if (rowHeight) {
            nrows = Math.floor((containerHeight - headerHeight - paginationHeight - controlbarHeight) / rowHeight)
            if (nrows < 1) {
                nrows = 1
            }
        }
        if (nrows === 0 && this.state.rows && this.state.rows.length) {
            // This is the case where the react table has not rendered completely, so we trigger a state change
            //   if we have row data.
            nrows = 1
            setTimeout(() => {
                this.forceUpdate()
            }, 1)
        }
        return nrows
    }

    buildRows(attributes) {
        // row data is nested in the properties object
        const rows = attributes.map(attrs => ({ ...attrs.properties, id: parseInt(attrs.id) }))
        this.setState({ fetchingAttributes: false, header: this.props.attributeTable.header, rows })
    }

    showStatistics = (columnName) => {
        const values = this.state.rows.map(r => parseFloat(r[columnName])).filter(n => n !== 0)
        this.setState({
            modelessDialog: 'STATISTICS',
            statValues: values,
            statTableColumn: columnName,
        })
    }

    openCalculator = (columnName) => {
        this.setState({
            modelessDialog: 'CALCULATOR',
            calcColumn: columnName,
        })
    }

    setEditable = () => {
        if (this.hasEM()) {
            // Request to cancel edits.
            this.cancelEdits()
        } else {
            // Put into edit mode
            this.createEM()
        }
        this.setState({ editable: this.hasEM() })
    }

    renderCell = (cellInfo) => {
        const colIndex = this.state.header.indexOf(cellInfo.column.id)
        const rowIndex = cellInfo.index
        let selected = false

        const col0 = this.state.cellSelection[0][0]
        const row0 = this.state.cellSelection[0][1]
        if (this.state.cellSelection.length === 1) {
            selected = colIndex === col0 && rowIndex === row0
        } else {
            const col1 = this.state.cellSelection[1][0]
            const row1 = this.state.cellSelection[1][1]
            const lowCol = (col0 < col1) ? col0 : col1
            const highCol = (col0 > col1) ? col0 : col1
            const lowRow = (row0 < row1) ? row0 : row1
            const highRow = (row0 > row1) ? row0 : row1
            selected = colIndex >= lowCol && colIndex <= highCol &&
                rowIndex >= lowRow && rowIndex <= highRow
        }

        const style = {
            backgroundColor: selected ? 'lightblue' : 'inherit',
            userSelect: 'none',
        }
        return (
            <div style={style}>{cellInfo.value}</div>
        )
    }

    renderEditableCell = (cellInfo) => {
        const rows = this.getEM().rows
        let val = rows[cellInfo.index][cellInfo.column.id]
        // Convert nulls and undefined to empty string for the input
        if (val === undefined || val === null) val = ''
        return (
            <TextField
                className={this.props.theme.attributeTableTextEdit}
                InputProps={{
                    classes: {
                        input: this.props.theme.attributeTableTextEdit,
                    },
                }}
                onBlur={(e) => {
                    const newValue = e.currentTarget.value
                    const row = rows[cellInfo.index]
                    this.getEM().addCellEdit(row.id, cellInfo.column.id, newValue)
                }}
                defaultValue={val}
                key={val}
            />)
    }

    saveEdits = () => {
        const editsToSave = this.getEM().saveEdits()
        if (editsToSave.length) {
            this.props.saveEdits(this.state.mapsourceName, this.state.layerName, editsToSave)
            // Turn off editing
            this.setState({
                editable: false,
                header: this.getEM().header,
                rows: this.getEM().rows,
                schema: this.getEM().schema,
            })
        }
    }

    cancelEdits = () => {
        this.destroyEM()
    }

    undoEdit = () => {
        this.getEM().undoLastEdit()
    }

    setFilter = (e) => {
        this.setState({ filter: e.currentTarget.value })
    }

    handleCellClick = (event, rowIndex, colIndex) => {
        if (!event.shiftKey) {
            this.setState({
                cellSelection: [[colIndex, rowIndex]],
            })
        } else {
            event.preventDefault()
            const firstColIndex = this.state.cellSelection[0][0]
            const firstRowIndex = this.state.cellSelection[0][1]
            this.setState({
                cellSelection: [
                    [firstColIndex, firstRowIndex],
                    [colIndex, rowIndex],
                ],
            })
        }
    }

    selectionString = () => {
        if (this.state.cellSelection.length === 1) {
            const i = this.state.cellSelection[0][1]
            const j = this.state.cellSelection[0][0]
            return this.state.rows[i][this.state.header[j]]
        }
        const col0 = this.state.cellSelection[0][0]
        const col1 = this.state.cellSelection[1][0]
        const row0 = this.state.cellSelection[0][1]
        const row1 = this.state.cellSelection[1][1]
        const lowCol = (col0 < col1) ? col0 : col1
        const highCol = (col0 > col1) ? col0 : col1
        const lowRow = (row0 < row1) ? row0 : row1
        const highRow = (row0 > row1) ? row0 : row1

        let resultString = ''

        for (let i = lowRow; i <= highRow; i += 1) {
            for (let j = lowCol; j <= highCol; j += 1) {
                const value = this.state.rows[i][this.state.header[j]]
                resultString += `${value}\t`
            }
            if (i !== highRow) resultString += '\n'
        }
        return resultString
    }

    handleCellCopy = (event) => {
        event.stopPropagation()
        copyToClipboard(this.selectionString())
    }

    handleFieldCalculate = (formula, targetColumn) => {
        this.getEM().fieldCalculate(formula, targetColumn)
    }

    openCreateLayer = () => {
        this.setState({ showCreateLayer: true })
    }

    closeCreateLayer = () => {
        this.setState({ showCreateLayer: false })
    }

    openJoinTable = () => {
        this.setState({ showJoinTable: true })
    }

    closeJoinTable = () => {
        this.setState({ showJoinTable: false })
    }

    createLayer = (mode, state) => {
        this.props.createLayerFromTable(this.state.mapsourceName, this.state.layerName, mode, state)
        this.closeCreateLayer()
    }

    joinTable = (joinField, sourceLayer, sourceField, joinLayerName) => {
        this.props.joinTable(this.state.mapsourceName, this.state.layerName, joinField, sourceLayer, sourceField, joinLayerName)
        this.closeJoinTable()
    }

    openAddColumn = () => {
        this.setState({ showAddColumn: true })
    }

    closeAddColumn = () => {
        this.setState({ showAddColumn: false })
    }

    addColumn = (name, type) => {
        this.getEM().addColumn(name, type)
        this.setState({
            header: this.getEM().header,
            schema: this.getEM().schema,
            showAddColumn: false,
        })
    }

    deleteColumn = (name) => {
        this.getEM().deleteColumn(name)
        this.setState({
            header: this.getEM().header,
            schema: this.getEM().schema,
        })
    }

    openRenameColumn = (name) => {
        this.setState({
            showRenameColumn: true,
            renameColumnName: name,
        })
    }

    renameColumn = (newName) => {
        const name = this.state.renameColumnName
        if (newName) {
            this.getEM().renameColumn(name, newName)
            this.setState({
                header: this.getEM().header,
                rows: this.getEM().rows,
                schema: this.getEM().schema,
            })
        }
        this.closeRenameColumn()
    }

    closeRenameColumn = () => {
        this.setState({
            showRenameColumn: false,
            renameColumnName: '',
        })
    }

    getAttributes() {
        if (this.state.geojson) {
            this.buildRows(this.state.geojson)
        } else {
            // Download from server
            let hasError = false
            fetch(`/er2_map/get_layer_attributes/?token=${this.props.token.value}`, {
                body: JSON.stringify({ mapsource_name: this.state.mapsourceName, layer: this.state.layerName }),
                method: 'POST',
            })
                .then((res) => {
                    hasError = res.status !== 200
                    if (hasError) return res.text()
                    return res.json()
                })
                .then((json) => {
                    if (hasError) {
                        this.props.appendToLogger([`Got failure to create layer: ${json}`])
                    } else {
                        this.buildRows(json.attributes)
                    }
                })
        }
    }

    openModeless = (dialog) => {
        this.setState({
            modelessDialog: dialog,
        })
    }

    closeModeless = () => {
        this.setState({
            modelessDialog: '',
        })
    }

    buildQuery = (queryString) => {
        console.warn('buildQuery not yet implemented')
    }

    render() {
        if (this.state.schema.properties) {
            const handleTableMenuClick = (event) => {
                this.setState({ tableMenuActionsAnchor: event.currentTarget })
            }

            const handleTableMenuClose = (action) => {
                this.setState({ tableMenuActionsAnchor: null })
                if (action === 'addColumn') {
                    this.openAddColumn()
                } else if (action === 'createLayer') {
                    this.openCreateLayer()
                } else if (action === 'export') {
                    console.warn(`Action is ${action}`)
                } else if (action === 'joinTable') {
                    this.openJoinTable()
                } else if (action === 'buildQuery') {
                    this.openModeless('QUERY_BUILDER')
                }
            }

            const columnWithContextMenu = (fname) => {
                const nicename = fname.replace(' ', '')
                const menuName = `column-actions-menu-${nicename}`
                const menuAnchorName = `columnMenuActions${nicename}Anchor`
                const handleColumnMenuClick = (event, anchorName) => {
                    this.setState({ [anchorName]: event.currentTarget })
                }
                const values = this.state.rows.map(r => parseFloat(r[fname])).filter(n => n !== 0)
                const jObj = jStat(values)

                const handleColumnMenuClose = (anchorName, columnName, action) => {
                    this.setState({ [anchorName]: null })
                    if (action === 'statistics') {
                        this.showStatistics(columnName)
                    } else if (action === 'calculate') {
                        this.openCalculator(columnName)
                    } else if (action === 'deleteColumn') {
                        this.deleteColumn(columnName)
                    } else if (action === 'renameColumn') {
                        this.openRenameColumn(columnName)
                    } else if (action !== undefined) {
                        console.warn(`Action is ${action}`)
                    }
                }

                return (
                    <React.Fragment>
                        <div>{fname}
                            <IconButton
                                aria-owns={this.state[menuAnchorName] ? menuName : null}
                                aria-haspopup="true"
                                onClick={e => handleColumnMenuClick(e, menuAnchorName)}
                                className={this.props.theme.littleButton}
                            >
                                <i className="fa fa-caret-down" />
                            </IconButton>
                            <Menu
                                id={menuName}
                                anchorEl={this.state[menuAnchorName]}
                                open={Boolean(this.state[menuAnchorName])}
                                onClose={() => handleColumnMenuClose(menuAnchorName)}
                            >
                                <MenuItem
                                    className={this.props.theme.attributeTableMenuItem}
                                    onClick={() => handleColumnMenuClose(menuAnchorName, fname, 'deleteColumn')}
                                >Delete
                                </MenuItem>
                                <MenuItem
                                    className={this.props.theme.attributeTableMenuItem}
                                    onClick={() => handleColumnMenuClose(menuAnchorName, fname, 'renameColumn')}
                                >Rename
                                </MenuItem>
                                <MenuItem
                                    className={this.props.theme.attributeTableMenuItem}
                                    onClick={() => handleColumnMenuClose(menuAnchorName, fname, 'calculate')}
                                >Calculate
                                </MenuItem>
                                <MenuItem
                                    className={this.props.theme.attributeTableMenuItem}
                                    onClick={() => handleColumnMenuClose(menuAnchorName, fname, 'statistics')}
                                    style={(isNaN(jObj.mean())) ? { display: 'none' } : {}}
                                >Statistics
                                </MenuItem>
                            </Menu>
                        </div>
                    </React.Fragment>
                )
            }

            const columns = this.state.header.map(fname => ({
                Header: columnWithContextMenu(fname),
                accessor: fname,
                Cell: this.state.editable ? this.renderEditableCell : this.renderCell,
            }))
            const rows = this.state.filter ? this.state.rows.filter((row) => {
                let ok = false
                Object.entries(row).forEach(([k, v]) => {
                    ok = ok || String(v).includes(this.state.filter)
                })
                return ok
            }) : this.state.rows

            const rowsPerPage = this.getRowsPerPage(this.props.height)
            return (
                <ErrorBoundary>
                    {this.state.modelessDialog === 'STATISTICS' &&
                        <Statistics
                            name={this.state.statTableColumn}
                            values={this.state.statValues}
                            statObj={jStat(this.state.statValues)}
                            onClose={this.closeModeless}
                            theme={this.props.theme}
                        />
                    }
                    {this.state.modelessDialog === 'CALCULATOR' &&
                        <Calculator
                            column={this.state.calcColumn}
                            onClose={this.closeModeless}
                            onCalculate={this.handleFieldCalculate}
                            schema={this.state.schema}
                            theme={this.props.theme}
                        />
                    }
                    {this.state.modelessDialog === 'QUERY_BUILDER' &&
                        <QueryBuilder
                            onClose={this.closeModeless}
                            onAccept={this.buildQuery}
                            schema={this.state.schema}
                            theme={this.props.theme}
                            rows={rows}
                            attributes={this.state.header}
                        />
                    }
                    <CreateLayer
                        rows={this.state.rows}
                        onClose={this.closeCreateLayer}
                        onAccept={this.createLayer}
                        schema={this.state.schema}
                        open={this.state.showCreateLayer}
                    />
                    <JoinTable
                        layer={this.props.attributeTable}
                        mapsources={this.props.mapsources}
                        onClose={this.closeJoinTable}
                        onAccept={this.joinTable}
                        open={this.state.showJoinTable}
                    />
                    <AddColumn
                        onClose={this.closeAddColumn}
                        onAccept={this.addColumn}
                        open={this.state.showAddColumn}
                    />
                    <RenameColumn
                        onClose={this.closeRenameColumn}
                        onAccept={this.renameColumn}
                        open={this.state.showRenameColumn}
                        name={this.state.renameColumnName}
                    />
                    <div
                        ref={r => (this.container = r)}
                        className={this.props.theme.attributeTableContainer}
                    >
                        <div
                            className={`attributeTableControlBar ${this.props.theme.attributeTableControlBar}`}
                            style={{
                                textAlign: 'right',
                            }}
                        >
                            <TextField
                                className={this.props.theme.attributeTableSearch}
                                id="search-text"
                                InputProps={{
                                    classes: {
                                        input: this.props.theme.attributeTableSearch,
                                    },
                                }}
                                label="Search"
                                margin="normal"
                                onChange={this.setFilter}
                            />
                            <div style={{ display: 'inline-block' }}>
                                <Button onClick={this.setEditable} className={this.props.theme.attributeTableButton}>{this.state.editable ? 'Cancel Edits' : 'Edit Cells'}</Button>
                                <Button onClick={this.saveEdits} className={this.props.theme.attributeTableButton} disabled={!this.hasEM()}>Save</Button>
                                <Button onClick={this.undoEdit} className={this.props.theme.attributeTableButton} disabled={!this.hasEM()}>Undo</Button>
                                <Button onClick={this.handleCellCopy} className={this.props.theme.attributeTableButton} disabled={this.state.cellSelection[0][0] === -1}>Copy</Button>
                                <IconButton
                                    onClick={handleTableMenuClick}
                                >
                                    <i className={'fa fa-bars'} />
                                </IconButton>
                                <Menu
                                    id="table-actions-menu"
                                    anchorEl={this.state.tableMenuActionsAnchor}
                                    className={this.props.theme.attributeTableMenuItem}
                                    open={Boolean(this.state.tableMenuActionsAnchor)}
                                    onClose={handleTableMenuClose}
                                >
                                    <MenuItem
                                        className={this.props.theme.attributeTableMenuItem}
                                        onClick={() => handleTableMenuClose('addColumn')}
                                    >Add Column</MenuItem>
                                    <MenuItem
                                        className={this.props.theme.attributeTableMenuItem}
                                        onClick={() => handleTableMenuClose('createLayer')}
                                    >Create Layer</MenuItem>
                                    <MenuItem
                                        className={this.props.theme.attributeTableMenuItem}
                                        onClick={() => handleTableMenuClose('export')}
                                    >Export
                                    </MenuItem>
                                    <MenuItem
                                        className={this.props.theme.attributeTableMenuItem}
                                        onClick={() => handleTableMenuClose('buildQuery')}
                                    >Query (Advanced Search)
                                    </MenuItem>
                                    <MenuItem
                                        className={this.props.theme.attributeTableMenuItem}
                                        onClick={() => handleTableMenuClose('joinTable')}
                                    >Join Table</MenuItem>
                                </Menu>
                            </div>
                        </div>
                        <ReactTable
                            className="-striped -highlight"
                            columns={columns}
                            data={rows}
                            defaultFilterMethod={this.filterMethod}
                            defaultPageSize={rowsPerPage}
                            getResizerProps={() => ({ style: { right: '-3px', width: '6px' } })}
                            getTdProps={(state, rowInfo, column) => ({
                                onClick: (e, handleOriginal) => {
                                    this.handleCellClick(e, rowInfo.index, this.state.header.indexOf(column.id))
                                    if (handleOriginal) {
                                        // Allow original event
                                        handleOriginal()
                                    }
                                },
                            })}
                            getTheadThProps={() => ({ style: { width: 0 } })}
                            getTheadGroupThProps={() => ({ style: { outline: 0 } })}
                            key={rowsPerPage}
                            pageSize={rowsPerPage}
                            showPageSizeOptions={false}
                        />
                    </div>
                </ErrorBoundary>
            )
        }
        return (
            <h3>Sorry, no attribute information is available</h3>
        )
    }
}


export default AttributeTable
